/**
 * Left-sidebar theme catalog.
 *
 * The actual colours live in styles/sidebarThemes.css (one CSS-variable block
 * per id). This file only describes the choices for the picker in Settings →
 * Appearance, plus the small helpers used to apply a theme before the server
 * round-trip completes.
 *
 * The ids MUST stay in sync with:
 *   - styles/sidebarThemes.css               (the [data-sidebar-theme] blocks)
 *   - backend accounts/models.py             (UserNavigationPreference.SIDEBAR_THEME_CHOICES)
 */

export type SidebarThemeId = 'midnight' | 'navy' | 'teal' | 'azure' | 'light';

// Default flipped midnight -> azure 2026-08-02. Must stay in sync with
// accounts.UserNavigationPreference.DEFAULT_SIDEBAR_THEME and with the
// bare `.app-sidebar` fallback block in styles/sidebarThemes.css.
export const DEFAULT_SIDEBAR_THEME: SidebarThemeId = 'azure';

export interface SidebarThemeDef {
  id: SidebarThemeId;
  name: string;
  description: string;
  /** Miniature preview swatch for the picker card. */
  preview: {
    surface: string;
    accent: string;
    text: string;
    activeText: string;
  };
}

export const SIDEBAR_THEMES: SidebarThemeDef[] = [
  {
    id: 'azure',
    name: 'Azure',
    description: 'Ocean blue — calm, between steel blue and Deep Teal, with pale-cyan accents.',
    preview: {
      surface: 'linear-gradient(160deg,#2a5a72 0%,#346c92 55%,#2a4e74 100%)',
      accent: 'linear-gradient(90deg,#6ed3e8,#cfeef2)',
      text: '#e4f0f6',
      activeText: '#ffffff',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep navy with cyan accents, matching the login screen.',
    preview: {
      surface: 'linear-gradient(160deg,#040b1a 0%,#07162b 45%,#02060d 100%)',
      accent: 'linear-gradient(90deg,#22d3ee,#4f46e5)',
      text: '#94a3b8',
      activeText: '#ffffff',
    },
  },
  {
    id: 'navy',
    name: 'Navy Solid',
    description: 'The same dark family, flat and quiet — no glow or texture.',
    preview: {
      surface: 'linear-gradient(180deg,#102037 0%,#0a1526 100%)',
      accent: 'linear-gradient(90deg,#38bdf8,#4f46e5)',
      text: '#9aa9bd',
      activeText: '#ffffff',
    },
  },
  {
    id: 'teal',
    name: 'Deep Teal',
    description: "A dark take on the platform's teal.",
    preview: {
      surface: 'linear-gradient(160deg,#04222b 0%,#073540 50%,#02171d 100%)',
      accent: 'linear-gradient(90deg,#2dd4bf,#0891b2)',
      text: '#8faab1',
      activeText: '#ffffff',
    },
  },
  {
    id: 'light',
    name: 'Classic Light',
    description: 'The original white sidebar.',
    preview: {
      surface: '#ffffff',
      accent: 'linear-gradient(90deg,#22d3ee,#4f46e5)',
      text: '#6b7280',
      activeText: '#1d4ed8',
    },
  },
];

const STORAGE_KEY = 'sidebar-theme';

/** Narrow an arbitrary value to a known theme id, falling back to the default. */
export function coerceSidebarTheme(value: unknown): SidebarThemeId {
  return SIDEBAR_THEMES.some((t) => t.id === value)
    ? (value as SidebarThemeId)
    : DEFAULT_SIDEBAR_THEME;
}

/**
 * Last known theme, cached in the browser.
 *
 * The saved theme is owned by the server so it follows the user between
 * devices, but reading it costs a round-trip. Caching the last value locally
 * lets the sidebar paint in the right colours immediately instead of flashing
 * the default and then repainting.
 */
export function readCachedSidebarTheme(): SidebarThemeId {
  if (typeof window === 'undefined') return DEFAULT_SIDEBAR_THEME;
  try {
    return coerceSidebarTheme(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_SIDEBAR_THEME;
  }
}

export function cacheSidebarTheme(theme: SidebarThemeId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode / quota — the server copy is still authoritative */
  }
}

export function clearCachedSidebarTheme(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
